export type ChargingSessionStage =
  | "idle"
  | "coverPrompt"
  | "chargingStarted"
  | "restRecommend"
  | "restActive";

export interface ChargingVideoRecommendation {
  category: string;
  durationMinutes: number;
  reason: string;
  title: string;
}

export interface ChargingSessionState {
  chargingPortOpen: boolean;
  currentBatteryLevel: number;
  estimatedChargeMinutes: number;
  estimatedFinishLabel: string;
  isCharging: boolean;
  restModeEnabled: boolean;
  stage: ChargingSessionStage;
  stationName: string;
  targetBatteryLevel: number;
  video: ChargingVideoRecommendation;
}

export const idleChargingSession: ChargingSessionState = {
  chargingPortOpen: false,
  currentBatteryLevel: 0,
  estimatedChargeMinutes: 0,
  estimatedFinishLabel: "--:--",
  isCharging: false,
  restModeEnabled: false,
  stage: "idle",
  stationName: "",
  targetBatteryLevel: 80,
  video: {
    category: "",
    durationMinutes: 0,
    reason: "",
    title: ""
  }
};

export const TARGET_BATTERY_LEVEL = 80;
export const USABLE_BATTERY_KWH = 76;
const AVERAGE_FAST_CHARGE_KW = 88;
const CHARGING_OVERHEAD_MINUTES = 4;

const videoCatalog: ChargingVideoRecommendation[] = [
  {
    category: "短片",
    durationMinutes: 18,
    reason: "适合短补能窗口，停车后可以完整看完",
    title: "城市光影短片集"
  },
  {
    category: "纪录片",
    durationMinutes: 32,
    reason: "和本次补能时长匹配，不影响后续出发",
    title: "山海一程"
  },
  {
    category: "访谈",
    durationMinutes: 41,
    reason: "接近预计补能窗口，适合休息模式观看",
    title: "未来出行会客厅"
  },
  {
    category: "电影精选",
    durationMinutes: 52,
    reason: "适合长补能窗口，系统会在充电完成前提醒",
    title: "公路晚风"
  }
];

export function createChargingSession({
  batteryLevel,
  stationName,
  now = new Date()
}: {
  batteryLevel: number;
  stationName: string;
  now?: Date;
}): ChargingSessionState {
  const metrics = buildChargingSessionMetrics(batteryLevel, now);

  return {
    ...idleChargingSession,
    ...metrics,
    stage: "coverPrompt",
    stationName
  };
}

export function updateChargingSessionBatteryLevel({
  batteryLevel,
  session,
  now = new Date()
}: {
  batteryLevel: number;
  session: ChargingSessionState;
  now?: Date;
}): ChargingSessionState {
  if (session.stage === "idle") {
    return session;
  }

  const metrics = buildChargingSessionMetrics(batteryLevel, now);

  if (
    session.currentBatteryLevel === metrics.currentBatteryLevel &&
    session.targetBatteryLevel === metrics.targetBatteryLevel &&
    session.estimatedChargeMinutes === metrics.estimatedChargeMinutes &&
    session.video.title === metrics.video.title
  ) {
    return session;
  }

  return {
    ...session,
    ...metrics
  };
}

function buildChargingSessionMetrics(batteryLevel: number, now: Date) {
  const currentBatteryLevel = clamp(Math.round(batteryLevel), 5, 100);
  const targetBatteryLevel = getTargetBatteryLevel(currentBatteryLevel);
  const estimatedChargeMinutes = estimateChargeMinutes(currentBatteryLevel, targetBatteryLevel);

  return {
    currentBatteryLevel,
    estimatedChargeMinutes,
    estimatedFinishLabel: formatFinishTime(now, estimatedChargeMinutes),
    targetBatteryLevel,
    video: pickVideoRecommendation(estimatedChargeMinutes)
  };
}

function getTargetBatteryLevel(currentBatteryLevel: number) {
  if (currentBatteryLevel >= 90) {
    return currentBatteryLevel;
  }

  return Math.max(TARGET_BATTERY_LEVEL, Math.min(90, currentBatteryLevel + 24));
}

function estimateChargeMinutes(currentBatteryLevel: number, targetBatteryLevel: number) {
  const missingPercent = Math.max(0, targetBatteryLevel - currentBatteryLevel) / 100;
  const energyNeededKwh = USABLE_BATTERY_KWH * missingPercent;
  const chargeMinutes = Math.ceil((energyNeededKwh / AVERAGE_FAST_CHARGE_KW) * 60 + CHARGING_OVERHEAD_MINUTES);
  return clamp(chargeMinutes, 16, 72);
}

function pickVideoRecommendation(estimatedChargeMinutes: number) {
  const playableVideos = videoCatalog.filter((video) => video.durationMinutes <= estimatedChargeMinutes - 3);
  return playableVideos.at(-1) ?? videoCatalog[0];
}

function formatFinishTime(now: Date, estimatedChargeMinutes: number) {
  const finishTime = new Date(now.getTime() + estimatedChargeMinutes * 60 * 1000);

  return new Intl.DateTimeFormat("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(finishTime);
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
